import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { apiErrorMessage, BACKEND_URL } from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const emptyArtist = {
    name: "", slug: "", role: "", credits: "", testimonial: "",
    photo_url: "", favorite_plugin_ids: [], display_order: 0, status: "published",
};

const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const resolveUrl = (raw) => {
    if (!raw) return "";
    if (raw.startsWith("/api/")) return `${BACKEND_URL}${raw}`;
    return raw;
};

export default function AdminArtists() {
    const [artists, setArtists] = useState([]);
    const [products, setProducts] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyArtist);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const load = async () => {
        try {
            const [a, p] = await Promise.all([
                api.get("/admin/artists"),
                api.get("/admin/products"),
            ]);
            setArtists(a.data || []);
            setProducts(p.data || []);
        } catch (err) {
            toast.error(apiErrorMessage(err));
        }
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditing(null);
        setForm({ ...emptyArtist, display_order: artists.length });
        setOpen(true);
    };

    const openEdit = (a) => {
        setEditing(a);
        setForm({
            name: a.name || "",
            slug: a.slug || "",
            role: a.role || "",
            credits: a.credits || "",
            testimonial: a.testimonial || "",
            photo_url: a.photo_url || "",
            favorite_plugin_ids: a.favorite_plugin_ids || [],
            display_order: a.display_order || 0,
            status: a.status || "published",
        });
        setOpen(true);
    };

    const togglePid = (pid) => {
        setForm((f) => ({
            ...f,
            favorite_plugin_ids: f.favorite_plugin_ids.includes(pid)
                ? f.favorite_plugin_ids.filter((x) => x !== pid)
                : [...f.favorite_plugin_ids, pid],
        }));
    };

    const uploadPhoto = async (file) => {
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) {
            toast.error("Image exceeds 8 MB");
            return;
        }
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/admin/artists/upload-photo", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setForm((f) => ({ ...f, photo_url: data.url }));
            toast.success("Photo uploaded");
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally {
            setUploading(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.slug.trim()) {
            toast.error("Name and slug are required");
            return;
        }
        try {
            const payload = { ...form, display_order: Number(form.display_order) || 0 };
            if (editing) {
                await api.patch(`/admin/artists/${editing.id}`, payload);
                toast.success("Artist updated");
            } else {
                await api.post("/admin/artists", payload);
                toast.success("Artist created");
            }
            setOpen(false); setEditing(null); setForm(emptyArtist);
            await load();
        } catch (err) {
            toast.error(apiErrorMessage(err));
        }
    };

    const remove = async (a) => {
        if (!window.confirm(`Delete artist "${a.name}"?`)) return;
        try {
            await api.delete(`/admin/artists/${a.id}`);
            toast.success("Artist deleted");
            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    return (
        <DashboardLayout role="admin">
            <div className="mb-8 flex items-center justify-between flex-wrap gap-4" data-testid="admin-artists-page">
                <div>
                    <div className="label-mono mb-3">/ Artists</div>
                    <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Artists & testimonials</h1>
                    <p className="text-avx-muted mt-2 max-w-2xl">
                        Producers, engineers and artists who use AVX Pro. They appear on the public <span className="text-avx-blue font-mono">/artistas</span> page.
                    </p>
                </div>
                <button onClick={openCreate} className="btn-primary px-5 py-2.5 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="create-artist-btn">
                    + New artist
                </button>
            </div>

            {artists.length === 0 ? (
                <div className="panel p-10 text-center text-avx-muted">
                    No artists yet. Add the first one to start showing real testimonials.
                </div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="admin-artists-grid">
                    {artists.map((a) => {
                        const photo = resolveUrl(a.photo_url);
                        return (
                            <div key={a.id} className="panel-elevated overflow-hidden flex flex-col" data-testid={`admin-artist-${a.id}`}>
                                <div className="aspect-[4/5] bg-avx-bg/60 border-b border-avx-line overflow-hidden">
                                    {photo ? (
                                        <img src={photo} alt={a.name} className="w-full h-full object-cover" loading="lazy" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <span className="font-heading font-black text-6xl text-avx-line">{a.name.charAt(0).toUpperCase()}</span>
                                        </div>
                                    )}
                                </div>
                                <div className="p-5 flex flex-col flex-1">
                                    <div className="flex items-start justify-between gap-2 mb-1">
                                        <h3 className="font-heading font-bold text-lg">{a.name}</h3>
                                        <span className={`text-[9px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm ${a.status === "published" ? "bg-avx-success/15 text-avx-success" : "bg-avx-line text-avx-muted"}`}>
                                            {a.status === "published" ? "published" : "draft"}
                                        </span>
                                    </div>
                                    <div className="text-xs text-avx-muted mb-2">{a.role}</div>
                                    {a.credits && <div className="text-xs font-bold mb-3 leading-snug">{a.credits}</div>}
                                    {a.testimonial && (
                                        <p className="text-xs text-avx-text/80 italic line-clamp-3 mb-3">“{a.testimonial}”</p>
                                    )}
                                    {a.favorite_plugins?.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mb-3">
                                            {a.favorite_plugins.map((p) => (
                                                <span key={p.id} className="text-[9px] font-mono uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm bg-avx-blue/10 text-avx-blue border border-avx-blue/30">
                                                    {p.name}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <div className="flex gap-2 pt-3 border-t border-avx-line mt-auto">
                                        <button onClick={() => openEdit(a)} className="text-xs font-mono uppercase tracking-[0.18em] text-avx-blue hover:underline" data-testid={`edit-artist-${a.id}`}>Edit</button>
                                        <button onClick={() => remove(a)} className="text-xs font-mono uppercase tracking-[0.18em] text-avx-danger hover:underline ml-auto" data-testid={`delete-artist-${a.id}`}>Delete</button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-avx-surface border-avx-line max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">{editing ? "Edit artist" : "New artist"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-4" data-testid="artist-form">
                        {/* Photo */}
                        <div>
                            <Label className="label-mono">Photo · vertical (4:5) ratio recommended · max 8 MB</Label>
                            <div className="mt-2 flex items-center gap-4">
                                <div className="w-24 h-32 bg-avx-bg border border-avx-line rounded-sm overflow-hidden flex items-center justify-center shrink-0">
                                    {form.photo_url ? (
                                        <img src={resolveUrl(form.photo_url)} alt="preview" className="w-full h-full object-cover" />
                                    ) : (
                                        <span className="text-[10px] font-mono text-avx-muted">NO PHOTO</span>
                                    )}
                                </div>
                                <div className="flex-1 space-y-2">
                                    <input
                                        ref={fileRef}
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        className="hidden"
                                        onChange={(e) => uploadPhoto(e.target.files?.[0])}
                                        data-testid="artist-photo-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => fileRef.current?.click()}
                                        disabled={uploading}
                                        className="btn-secondary px-4 py-2 text-xs font-mono uppercase tracking-[0.2em] rounded-sm"
                                        data-testid="artist-photo-upload-btn"
                                    >
                                        {uploading ? "Uploading…" : form.photo_url ? "Replace photo" : "Upload photo"}
                                    </button>
                                    {form.photo_url && (
                                        <button
                                            type="button"
                                            onClick={() => setForm({ ...form, photo_url: "" })}
                                            className="text-[10px] font-mono uppercase tracking-[0.18em] text-avx-danger hover:underline ml-3"
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="label-mono">Name</Label>
                                <Input className="mt-2 bg-avx-bg border-avx-line h-11" value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value, slug: editing ? form.slug : slugify(e.target.value) })} required data-testid="artist-name" />
                            </div>
                            <div>
                                <Label className="label-mono">Slug</Label>
                                <Input className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.slug}
                                    onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} required data-testid="artist-slug" />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="label-mono">Role</Label>
                                <Input className="mt-2 bg-avx-bg border-avx-line h-11" value={form.role}
                                    onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Producer, Mix Engineer" data-testid="artist-role" />
                            </div>
                            <div>
                                <Label className="label-mono">Credits</Label>
                                <Input className="mt-2 bg-avx-bg border-avx-line h-11" value={form.credits}
                                    onChange={(e) => setForm({ ...form, credits: e.target.value })} placeholder="Bad Bunny, Akon, J Balvin" data-testid="artist-credits" />
                            </div>
                        </div>

                        <div>
                            <Label className="label-mono">Testimonial · max 800 characters</Label>
                            <Textarea rows={4} className="mt-2 bg-avx-bg border-avx-line" value={form.testimonial}
                                onChange={(e) => setForm({ ...form, testimonial: e.target.value })} maxLength={800}
                                placeholder="Short quote about how they use AVX plugins in their work." data-testid="artist-testimonial" />
                            <div className="text-[10px] font-mono text-avx-muted mt-1 text-right">{form.testimonial.length}/800</div>
                        </div>

                        <div>
                            <Label className="label-mono">Favorite plugins · shown as linkable tags</Label>
                            <div className="mt-2 panel p-3 max-h-48 overflow-y-auto space-y-1.5" data-testid="artist-plugins-picker">
                                {products.length === 0 ? (
                                    <div className="text-avx-muted text-sm">No products yet.</div>
                                ) : products.map((p) => {
                                    const checked = form.favorite_plugin_ids.includes(p.id);
                                    return (
                                        <label key={p.id} className={`flex items-center gap-3 p-2 rounded-sm border cursor-pointer transition-colors ${checked ? "bg-avx-blue/10 border-avx-blue/40" : "border-transparent hover:bg-avx-bg/40"}`}>
                                            <input type="checkbox" checked={checked} onChange={() => togglePid(p.id)} className="accent-avx-blue" data-testid={`artist-pick-${p.id}`} />
                                            <span className="text-sm font-mono">{p.name}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="label-mono">Status</Label>
                                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                    <SelectTrigger className="mt-2 bg-avx-bg border-avx-line h-11 font-mono"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="published">Publicado</SelectItem>
                                        <SelectItem value="draft">Draft (hidden)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="label-mono">Order (lower = first)</Label>
                                <Input type="number" className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.display_order}
                                    onChange={(e) => setForm({ ...form, display_order: e.target.value })} data-testid="artist-order" />
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 pt-2 border-t border-avx-line">
                            <button type="button" onClick={() => setOpen(false)} className="btn-secondary px-4 py-2 text-xs font-mono uppercase tracking-[0.2em] rounded-sm">Cancel</button>
                            <button type="submit" className="btn-primary px-6 py-2 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="artist-submit">
                                {editing ? "Save changes" : "Create artist"}
                            </button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
